import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Server,
  Cpu,
  MemoryStick,
  HardDrive,
  Shield,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Users,
  Clock,
  ShieldX,
} from 'lucide-react';
import { api } from '@/api/client';
import { clsx } from 'clsx';
import type { DashboardOverview, SecurityDashboard, HealthOverview, VpsHealthSummary, VpsStatus, ThreatLevel } from '@pulse/shared';

type Tab = 'main' | 'security' | 'external';

const statusIcons: Record<VpsStatus, typeof CheckCircle> = {
  online: CheckCircle,
  offline: XCircle,
  degraded: AlertTriangle,
  unknown: HelpCircle,
  maintenance: HelpCircle,
};

const statusColors: Record<VpsStatus, string> = {
  online: 'text-status-online',
  offline: 'text-status-offline',
  degraded: 'text-status-degraded',
  unknown: 'text-status-unknown',
  maintenance: 'text-status-unknown',
};

export function Dashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('main');

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-gray-800 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('main')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'main'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Server className="h-4 w-4" />
            Main
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'security'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Shield className="h-4 w-4" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('external')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'external'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Globe className="h-4 w-4" />
            API Health
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'main' && <MainTab />}
      {activeTab === 'security' && <SecurityTab />}
      {activeTab === 'external' && <ExternalTab />}
    </div>
  );
}

const REFRESH_INTERVAL = 5000;

function MainTab() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard', 'main'],
    queryFn: () => api.get<DashboardOverview>('/dashboard/main'),
    refetchInterval: REFRESH_INTERVAL,
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const prevUpdatedAt = useRef(dataUpdatedAt);

  // Trigger animation reset when data updates
  useEffect(() => {
    if (dataUpdatedAt !== prevUpdatedAt.current) {
      prevUpdatedAt.current = dataUpdatedAt;
      setRefreshKey((k) => k + 1);
    }
  }, [dataUpdatedAt]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !data) {
    return <ErrorState message="Failed to load dashboard" />;
  }

  if (data.vpsStatuses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Server className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg mb-2">No VPS servers configured</p>
        <Link to="/vps/new" className="btn-primary mt-4">
          Add your first VPS
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Live indicator */}
      <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span>Live</span>
        <span className="text-gray-600">•</span>
        <span>Updates every 5s</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.vpsStatuses.map((vps) => (
          <VpsCard key={`${vps.id}-${refreshKey}`} vps={vps} refreshKey={refreshKey} />
        ))}
      </div>
    </div>
  );
}

function VpsCard({ vps, refreshKey: _refreshKey }: { vps: DashboardOverview['vpsStatuses'][0]; refreshKey: number }) {
  const Icon = statusIcons[vps.status];
  const statusColor = statusColors[vps.status];

  const getBarColor = (value: number | null) => {
    if (value == null) return 'bg-gray-700';
    if (value >= 90) return 'bg-status-offline';
    if (value >= 75) return 'bg-status-degraded';
    return 'bg-pulse-500';
  };

  const formatBytes = (bytes: number | null): string => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${Math.round(bytes)} B/s`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB/s`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB/s`;
  };

  return (
    <Link
      to={`/vps/${vps.id}`}
      className="card hover:border-gray-700 transition-colors group progress-border progress-border-animate"
      style={{ '--duration': `${REFRESH_INTERVAL}ms` } as React.CSSProperties}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg bg-gray-800', statusColor)}>
              <Server className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-medium text-gray-100 group-hover:text-pulse-400 transition-colors">
                {vps.name}
              </h3>
              <p className="text-xs text-gray-500">{vps.ipAddress}</p>
            </div>
          </div>
          <div className={clsx('flex items-center gap-1.5', statusColor)}>
            <Icon className="h-4 w-4" />
            <span className="text-sm capitalize">{vps.status}</span>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="p-4 space-y-4">
        {/* CPU */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Cpu className="h-3.5 w-3.5" />
              <span>CPU</span>
            </div>
            <span className="text-sm font-medium">
              {vps.cpuPercent != null ? `${vps.cpuPercent.toFixed(1)}%` : '-'}
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={clsx('h-full transition-all', getBarColor(vps.cpuPercent))}
              style={{ width: `${Math.min(100, vps.cpuPercent ?? 0)}%` }}
            />
          </div>
        </div>

        {/* RAM */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <MemoryStick className="h-3.5 w-3.5" />
              <span>RAM</span>
            </div>
            <span className="text-sm font-medium">
              {vps.ramPercent != null ? `${vps.ramPercent.toFixed(1)}%` : '-'}
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={clsx('h-full transition-all', getBarColor(vps.ramPercent))}
              style={{ width: `${Math.min(100, vps.ramPercent ?? 0)}%` }}
            />
          </div>
        </div>

        {/* Disk */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <HardDrive className="h-3.5 w-3.5" />
              <span>Disk</span>
            </div>
            <span className="text-sm font-medium">
              {vps.diskPercent != null ? `${vps.diskPercent.toFixed(1)}%` : '-'}
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={clsx('h-full transition-all', getBarColor(vps.diskPercent))}
              style={{ width: `${Math.min(100, vps.diskPercent ?? 0)}%` }}
            />
          </div>
        </div>

        {/* Network */}
        {(vps.networkIn != null || vps.networkOut != null) && (
          <div className="pt-2 border-t border-gray-800">
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
              <Activity className="h-3.5 w-3.5" />
              <span>Network</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5 text-green-400">
                <ArrowDownRight className="h-3.5 w-3.5" />
                <span>{formatBytes(vps.networkIn)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-blue-400">
                <ArrowUpRight className="h-3.5 w-3.5" />
                <span>{formatBytes(vps.networkOut)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-800 bg-gray-900/50">
        <p className="text-xs text-gray-500">
          {vps.lastSeenAt ? `Last seen ${formatRelativeTime(vps.lastSeenAt)}` : 'Never connected'}
        </p>
      </div>
    </Link>
  );
}

function SecurityTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'security'],
    queryFn: () => api.get<SecurityDashboard>('/dashboard/security'),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !data) {
    return <ErrorState message="Failed to load security data" />;
  }

  if (data.vpsSecurityData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Shield className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg mb-2">No VPS servers configured</p>
        <Link to="/vps/new" className="btn-primary mt-4">
          Add your first VPS
        </Link>
      </div>
    );
  }

  const totalSessions = data.vpsSecurityData.reduce((s, v) => s + v.activeSessions, 0);
  const totalFailed = data.vpsSecurityData.reduce((s, v) => s + v.failedAttempts, 0);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Users className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Successful Logins (7d)</p>
              <p className="text-lg font-semibold text-green-400">{totalSessions.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Blocked Probes (7d)</p>
              <p className="text-lg font-semibold text-gray-100">{totalFailed.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className={clsx(
              'p-2 rounded-lg',
              data.serversNeedingAttention > 0 ? 'bg-yellow-500/10' : 'bg-green-500/10'
            )}>
              <AlertTriangle className={clsx(
                'h-5 w-5',
                data.serversNeedingAttention > 0 ? 'text-yellow-400' : 'text-green-400'
              )} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Servers Needing Attention</p>
              <p className={clsx(
                'text-lg font-semibold',
                data.serversNeedingAttention > 0 ? 'text-yellow-400' : 'text-green-400'
              )}>
                {data.serversNeedingAttention} <span className="text-sm text-gray-500">/ {data.vpsSecurityData.length}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* VPS Security Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.vpsSecurityData.map((vps) => (
          <SecurityCard key={vps.vpsId} vps={vps} />
        ))}
      </div>
    </div>
  );
}

const threatBadgeStyles: Record<ThreatLevel, { bg: string; text: string; label: string }> = {
  normal: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Normal' },
  elevated: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Elevated' },
  concerning: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Concerning' },
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Critical' },
};

function SecurityCard({ vps }: { vps: SecurityDashboard['vpsSecurityData'][0] }) {
  const badge = threatBadgeStyles[vps.threatLevel];

  return (
    <Link
      to={`/vps/${vps.vpsId}`}
      className="card hover:border-gray-700 transition-colors group"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', badge.bg, badge.text)}>
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-medium text-gray-100 group-hover:text-pulse-400 transition-colors">
                {vps.vpsName}
              </h3>
              <p className="text-xs text-gray-500">{vps.ipAddress}</p>
            </div>
          </div>
          <span className={clsx('status-badge', badge.bg, badge.text)}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="p-4 space-y-3">
        {/* Threat reason */}
        {vps.threatReasons.length > 0 && (
          <p className="text-xs text-gray-400">{vps.threatReasons[0]}</p>
        )}

        {/* Suspicious successes warning */}
        {vps.suspiciousSuccesses > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
            <span className="text-xs text-red-400">
              {vps.suspiciousSuccesses} login(s) from IPs with prior failures
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Users className="h-3.5 w-3.5" />
            <span>Logins (7d)</span>
          </div>
          <span className="text-sm font-medium text-green-400">
            {vps.activeSessions.toLocaleString()}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ShieldX className="h-3.5 w-3.5" />
            <span>Failed (7d)</span>
          </div>
          <span className="text-sm font-medium text-gray-300">
            {vps.failedAttempts.toLocaleString()}
            {vps.uniqueAttackerIps > 0 && (
              <span className="text-xs text-gray-500 ml-1">
                from {vps.uniqueAttackerIps.toLocaleString()} IPs
              </span>
            )}
          </span>
        </div>

        {/* Last Session Info */}
        {vps.lastSession && (
          <div className="pt-3 border-t border-gray-800">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Clock className="h-3.5 w-3.5" />
                <span>Last Login</span>
              </div>
              <span className="text-xs text-gray-500">
                {formatRelativeTime(vps.lastSession.timestamp)}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="font-mono text-xs text-gray-400">{vps.lastSession.sourceIp}</span>
              <span className="text-sm">
                {vps.lastSession.geoCountry && vps.lastSession.geoCountry !== 'XX'
                  ? `${countryCodeToFlag(vps.lastSession.geoCountry)} ${vps.lastSession.geoCountry}`
                  : ''}
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function ExternalTab() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard', 'health'],
    queryFn: () => api.get<HealthOverview>('/dashboard/health'),
    refetchInterval: REFRESH_INTERVAL,
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const prevUpdatedAt = useRef(dataUpdatedAt);

  useEffect(() => {
    if (dataUpdatedAt !== prevUpdatedAt.current) {
      prevUpdatedAt.current = dataUpdatedAt;
      setRefreshKey((k) => k + 1);
    }
  }, [dataUpdatedAt]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !data) {
    return <ErrorState message="Failed to load health data" />;
  }

  if (data.totalEndpoints === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Globe className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg mb-2">No endpoints configured</p>
        <p className="text-sm">Add websites to your VPS entries to start monitoring</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live indicator */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span>Live</span>
        <span className="text-gray-600">&bull;</span>
        <span>Updates every 5s</span>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Total Endpoints</p>
          <p className="text-2xl font-semibold">{data.totalEndpoints}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Online</p>
          <p className="text-2xl font-semibold text-green-400">{data.totalUp}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Down</p>
          <p className="text-2xl font-semibold text-status-offline">{data.totalDown}</p>
        </div>
      </div>

      {/* Per-VPS Health Cards */}
      {data.vpsSummaries.map((vps) => (
        <HealthCard key={`${vps.vpsId}-${refreshKey}`} vps={vps} />
      ))}
    </div>
  );
}

function HealthCard({ vps }: { vps: VpsHealthSummary }) {
  const allUp = vps.overallDown === 0;

  return (
    <div
      className="card progress-border progress-border-animate"
      style={{ '--duration': `${REFRESH_INTERVAL}ms` } as React.CSSProperties}
    >
      <div className="card-header">
        <div className="flex items-center gap-3">
          <div className={clsx('p-2 rounded-lg', allUp ? 'bg-green-500/10' : 'bg-red-500/10')}>
            <Server className={clsx('h-4 w-4', allUp ? 'text-green-400' : 'text-red-400')} />
          </div>
          <Link to={'/vps/' + vps.vpsId} className="font-medium text-gray-100 hover:text-pulse-400 transition-colors">
            {vps.vpsName}
          </Link>
          <span className={clsx(
            'status-badge ml-auto',
            allUp ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
          )}>
            {vps.overallUp}/{vps.endpoints.length} up
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Response Time</th>
              <th>Avg (1h)</th>
              <th>Uptime</th>
              <th>Last Check</th>
            </tr>
          </thead>
          <tbody>
            {vps.endpoints.map((ep) => (
              <tr key={ep.url}>
                <td>
                  <a
                    href={ep.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pulse-400 hover:text-pulse-300 font-mono text-sm"
                  >
                    {ep.url.replace(/^https?:\/\//, '')}
                  </a>
                </td>
                <td>
                  {ep.isUp ? (
                    <span className="flex items-center gap-1.5 text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      {ep.statusCode}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-red-400">
                      <XCircle className="h-4 w-4" />
                      {ep.statusCode || 'Error'}
                    </span>
                  )}
                </td>
                <td>
                  <span className={clsx(
                    'font-mono text-sm',
                    ep.responseTimeMs < 300 ? 'text-green-400' :
                    ep.responseTimeMs < 1000 ? 'text-yellow-400' : 'text-red-400'
                  )}>
                    {ep.responseTimeMs}ms
                  </span>
                </td>
                <td>
                  <span className="font-mono text-sm text-gray-400">{ep.avgResponseMs}ms</span>
                </td>
                <td>
                  <span className={clsx(
                    'font-medium',
                    ep.uptimePercent >= 99 ? 'text-green-400' :
                    ep.uptimePercent >= 95 ? 'text-yellow-400' : 'text-red-400'
                  )}>
                    {ep.uptimePercent}%
                  </span>
                </td>
                <td className="text-gray-500 text-sm">
                  {ep.lastCheckedAt ? formatRelativeTime(ep.lastCheckedAt) : 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pulse-400" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
      <AlertTriangle className="h-8 w-8 mb-2" />
      <p>{message}</p>
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

// Convert ISO 3166-1 alpha-2 country code to flag emoji
function countryCodeToFlag(code: string | null): string | null {
  if (!code || code === 'XX' || code.length !== 2) return null;
  const codePoints = code
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
